import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Search, Home, UtensilsCrossed, Instagram, Facebook, MessageCircle } from 'lucide-react';
import clsx from 'clsx';
import { NAV_LINKS } from '../../lib/constants';
import { useFilterStore } from '../../stores/filterStore';
import { useSiteSettings } from '../../services/settingsService';

// TikTok icon component
function TikTokIcon({ size = 16, className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );
}

/**
 * Yelp-style top nav — transparent on home (overlays hero image),
 * solid red on all other pages.
 */
export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const setSearch = useFilterStore((s) => s.setSearch);
  const { data: settings } = useSiteSettings();
  const isHome = location.pathname === '/';

  const phone = settings?.whatsapp_phone || '254700000000';

  // Social media links
  const socialLinks = [
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      url: `https://wa.me/${phone.replace(/\D/g, '')}`,
    },
    {
      name: 'Instagram',
      icon: Instagram,
      url: 'https://instagram.com/smothersrestaurant',
    },
    {
      name: 'Facebook',
      icon: Facebook,
      url: 'https://facebook.com/smothersrestaurant',
    },
    {
      name: 'TikTok',
      icon: TikTokIcon,
      url: 'https://www.tiktok.com/@smothers.restaurants',
    },
  ];

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(localSearch);
    if (location.pathname !== '/menu') {
      navigate('/menu');
    }
  };

  return (
    <nav
      className={clsx(
        'fixed top-0 left-0 right-0 z-50 transition-colors duration-300',
        isHome ? 'bg-transparent' : 'bg-brand-red shadow-md'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4 h-14 sm:h-16">
          {/* Logo — large visible text, no background */}
          <Link to="/" className="flex items-center no-underline flex-shrink-0">
            <span className="text-2xl sm:text-3xl font-extrabold text-brand-red tracking-tight italic">
              smothers
            </span>
          </Link>

          {/* Search bar in nav — Yelp-style */}
          <form
            onSubmit={handleSearchSubmit}
            className="hidden md:flex flex-1 max-w-md"
          >
            <div className="flex w-full rounded-lg overflow-hidden shadow-sm">
              <input
                type="text"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder="burgers, sides, drinks..."
                className="flex-1 px-5 py-3 text-sm text-brand-dark bg-brand-white border-0 focus:outline-none placeholder:text-ui-muted"
              />
              <button
                type="submit"
                className="px-5 bg-brand-red hover:bg-red-700 transition-colors flex items-center"
              >
                <Search size={18} className="text-brand-white" />
              </button>
            </div>
          </form>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-1 ml-auto">
            {NAV_LINKS.map((link) => {
              const Icon = link.icon === 'Home' ? Home : UtensilsCrossed;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-1.5 rounded text-sm font-semibold transition-colors',
                    location.pathname === link.to
                      ? 'bg-brand-white/20 text-brand-white'
                      : 'text-brand-white/80 bg-brand-red hover:text-brand-white'
                  )}
                >
                  <Icon size={24} />
                  {link.label}
                </Link>
              );
            })}
            
            {/* Social Icons */}
            <div className="flex items-center gap-1.5 ml-3 pl-3 border-l border-brand-white/30">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={clsx(
                      'w-9 h-9 flex items-center justify-center rounded-lg transition-all',
                      isHome 
                        ? 'bg-brand-white/10 text-brand-white hover:bg-brand-white/20 hover:scale-110'
                        : 'bg-brand-white/20 text-brand-white hover:bg-brand-white/30 hover:scale-110'
                    )}
                    aria-label={social.name}
                    title={social.name}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded text-brand-white hover:bg-brand-white/10 ml-auto"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={clsx(
              'md:hidden overflow-hidden border-t border-brand-white/10',
              isHome ? 'bg-brand-dark/80 backdrop-blur-md' : 'bg-brand-red'
            )}
          >
            <div className="px-4 py-3 space-y-2">
              {/* Mobile search */}
              <form onSubmit={handleSearchSubmit}>
                <div className="flex rounded-lg overflow-hidden shadow-sm">
                  <input
                    type="text"
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    placeholder="Search products..."
                    className="flex-1 px-5 py-3 text-sm text-brand-dark bg-brand-white focus:outline-none placeholder:text-ui-muted"
                  />
                  <button type="submit" className="px-5 bg-brand-red hover:bg-red-700 transition-colors flex items-center">
                    <Search size={18} className="text-brand-white" />
                  </button>
                </div>
              </form>

              {NAV_LINKS.map((link) => {
                const Icon = link.icon === 'Home' ? Home : UtensilsCrossed;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={clsx(
                      'flex items-center gap-2 px-4 py-2.5 rounded text-sm font-semibold',
                      location.pathname === link.to
                        ? 'bg-brand-white/20 text-brand-white'
                        : 'text-brand-white/80 hover:bg-brand-white/10'
                    )}
                  >
                    <Icon size={16} />
                    {link.label}
                  </Link>
                );
              })}

              {/* Mobile Social Icons */}
              <div className="flex items-center justify-center gap-3 pt-4 mt-4 border-t border-brand-white/20">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-white/15 text-brand-white hover:bg-brand-white/25 hover:scale-110 transition-all"
                      aria-label={social.name}
                      title={social.name}
                    >
                      <Icon size={20} />
                    </a>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
